import React from "react";
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Divider,
  Button,
  Stack,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { Dayjs } from "dayjs";
import { brand } from "../../../theme/themePrimitives";

export type FilterItem = {
  id: string;
  name: string;
  checked?: boolean;
};

export type FilterGroup = {
  title: string;
  items: FilterItem[];
};

type Props = {
  filtersList: FilterGroup[];
  showFilter: boolean;
  setShowFilter: (v: boolean) => void;
  handleCheckboxChange: (groupIndex: number, itemIndex: number) => void;
  saveFilters: () => void;
  clearFilters: () => void;
  dateFrom: Dayjs | null;
  dateTo: Dayjs | null;
  onDateFromChange: (date: Dayjs | null) => void;
  onDateToChange: (date: Dayjs | null) => void;
  openFromDatePicker?: boolean;
  onOpenFromDatePickerChange?: () => void;
  onCloseFromDatePicker?: () => void;
  openToDatePicker?: boolean;
  onOpenToDatePickerChange?: () => void;
  onCloseToDatePicker?: () => void;
};

export const Filter: React.FC<Props> = ({
  filtersList,
  showFilter,
  setShowFilter,
  handleCheckboxChange,
  saveFilters,
  clearFilters,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  openFromDatePicker,
  onOpenFromDatePickerChange,
  onCloseFromDatePicker,
  openToDatePicker,
  onOpenToDatePickerChange,
  onCloseToDatePicker,
}) => {
  return (
    <Drawer
      id="filehub-filter-drawer"
      anchor="right"
      open={showFilter}
      onClose={() => setShowFilter(false)}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: "white",
          },
        },
      }}
    >
      <Box
        sx={{
          width: 320,
          py: 1,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">Filters</Typography>
          <IconButton
            id="filehub-filter-close-button"
            onClick={() => setShowFilter(false)}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ flex: 1, overflow: "auto" }}>
          {filtersList.map((group, groupIndex) => (
            <Box key={group.title} sx={{ m: 1 }}>
              <Typography sx={{ mb: 1, fontWeight: 600 }}>
                {group.title}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {group.items.map((item, itemIndex) => (
                  <FormControlLabel
                    key={item.id}
                    control={
                      <Checkbox
                        checked={!!item.checked}
                        onChange={() =>
                          handleCheckboxChange(groupIndex, itemIndex)
                        }
                        size="small"
                      />
                    }
                    label={item.name}
                  />
                ))}
              </Box>
            </Box>
          ))}
          <Divider sx={{ my: 1 }} />
          <Box sx={{ m: 1, mt: 2 }}>
            <Typography sx={{ mb: 1, fontWeight: 600 }}>Date</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Stack direction="row" spacing={1} alignItems="center">
                <DatePicker
                  label="From"
                  value={dateFrom}
                  onChange={(value) => onDateFromChange(value as Dayjs | null)}
                  format="DD-MMM-YYYY"
                  maxDate={dateTo || undefined}
                  slots={{ openPickerIcon: CalendarTodayOutlinedIcon }}
                  open={openFromDatePicker}
                  onOpen={() => onOpenFromDatePickerChange?.()}
                  onClose={() => onCloseFromDatePicker?.()}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "outlined",
                      placeholder: "From",
                      onClick: () => onOpenFromDatePickerChange?.(),
                      InputProps: {
                        readOnly: true,
                        sx: {
                          "& .MuiInputAdornment-root button": {
                            border: "none",
                            padding: 0,
                            background: "transparent",
                            "&:hover": { background: "transparent" },
                          },
                        },
                      },
                    },
                    openPickerButton: { sx: { border: "none" } },
                    openPickerIcon: { sx: { width: "15px" } },
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiSvgIcon-root": { fontSize: "1.5rem" },
                  }}
                />
                <Typography sx={{ color: "#344054", fontWeight: 500 }}>
                  -
                </Typography>
                <DatePicker
                  label="To"
                  value={dateTo}
                  onChange={(value) => onDateToChange(value as Dayjs | null)}
                  open={openToDatePicker}
                  onOpen={() => onOpenToDatePickerChange?.()}
                  onClose={() => onCloseToDatePicker?.()}
                  format="DD-MMM-YYYY"
                  minDate={dateFrom || undefined}
                  slots={{ openPickerIcon: CalendarTodayOutlinedIcon }}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "outlined",
                      placeholder: "To",
                      onClick: () => onOpenToDatePickerChange?.(),
                      InputProps: {
                        readOnly: true,
                        sx: {
                          "& .MuiInputAdornment-root button": {
                            border: "none",
                            padding: 0,
                            background: "transparent",
                            "&:hover": { background: "transparent" },
                          },
                        },
                      },
                    },
                    openPickerButton: { sx: { border: "none" } },
                    openPickerIcon: { sx: { width: "15px" } },
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiSvgIcon-root": { fontSize: "1.5rem" },
                  }}
                />
              </Stack>
            </LocalizationProvider>
          </Box>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Stack direction="row" spacing={1} justifyContent="space-between">
          <Button
            sx={{
              px: 2,
              borderRadius: "12px",
              backgroundColor: `${brand[600]} !important`,
              color: `white`,
              "&:hover": {
                bgcolor: `${brand[700]} !important`,
                textDecoration: "underline",
              },
            }}
            onClick={() => {
              saveFilters();
              setShowFilter(false);
            }}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              clearFilters();
            }}
          >
            Clear all
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default Filter;
